import { useState, useRef } from 'react';
import { ArrowLeft, Play, X, Film } from 'lucide-react';
import { ytEmbedUrl } from '../utils/youtube';

const VIDEOS = Array.from({ length: 27 }, (_, i) => ({
  src: `/historia/video-${String(i + 1).padStart(2, '0')}.mp4`,
  num: i + 1,
}));

const POSTER = '/historia/image-01.jpg';

const VideoCard = ({ video, onOpen }) => {
  const ref = useRef(null);
  return (
    <button
      data-testid={`video-card-${video.num}`}
      onClick={() => onOpen(video)}
      className="group relative aspect-video bg-[#0f1629] border border-white/10 rounded-xl overflow-hidden hover:border-[#c9a227]/60 hover:scale-[1.02] transition-all shadow-lg"
    >
      <video
        ref={ref}
        src={video.src}
        preload="metadata"
        muted
        playsInline
        className="w-full h-full object-cover pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 pointer-events-none" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-16 h-16 rounded-full bg-[#c9a227] flex items-center justify-center shadow-[0_8px_30px_rgba(201,162,39,0.5)] group-hover:scale-110 transition-transform">
          <Play className="h-7 w-7 text-[#0a0a14] fill-[#0a0a14] ml-1" />
        </div>
      </div>
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none">
        <span className="bg-black/60 backdrop-blur text-white text-xs font-mono font-bold px-2.5 py-1 rounded">
          #{String(video.num).padStart(2, '0')}
        </span>
        <span className="bg-[#c9a227] text-[#0a0a14] text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded">
          HISTORIA
        </span>
      </div>
    </button>
  );
};

const HistoriaEnVideosPage = () => {
  const [active, setActive] = useState(null);

  return (
    <div className="min-h-screen bg-[#1a1a2e]" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }} data-testid="historia-videos-page">
      {/* HEADER */}
      <header className="bg-[#0f1629] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <a
            href="/"
            data-testid="back-home"
            className="flex items-center gap-2 text-slate-300 hover:text-white font-medium text-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Regresar
          </a>
          <div className="w-px h-5 bg-white/20" />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Film className="h-4 w-4 text-[#c9a227] flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-white text-sm truncate">La Historia en Videos</p>
              <p className="text-slate-500 text-[10px] truncate">27 videos · Carlos Miami Fernandez · Club Viajemos / Travel Empire</p>
            </div>
          </div>
          <span className="bg-[#c9a227]/15 text-[#c9a227] text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-[#c9a227]/30 hidden sm:inline-block">
            27 VIDEOS
          </span>
        </div>
      </header>

      {/* HERO */}
      <section className="relative px-4 pt-14 pb-10 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#c9a227]/10 rounded-full blur-[160px] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="inline-block bg-[#c9a227]/15 text-[#c9a227] text-[11px] uppercase tracking-[3px] px-4 py-1.5 rounded-full font-mono mb-5 border border-[#c9a227]/30">
            EL CAMINO DE UN EMPRENDEDOR
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight mb-5">
            La historia<br />
            <span className="text-[#c9a227]">en videos</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Los momentos, aprendizajes y decisiones que construyeron a <span className="text-white font-bold">Carlos Miami Fernandez</span> como referente en la industria de viajes. Mira el camino real, sin filtros.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="px-4 pb-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {VIDEOS.map((v) => (
              <VideoCard key={v.num} video={v} onOpen={setActive} />
            ))}
          </div>
        </div>
      </section>

      {/* LAS ESTRELLAS DE TV NOS AVALAN */}
      <section data-testid="tv-stars-section" className="px-4 py-16 border-t border-white/10 bg-[#0f1629]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#c9a227]/15 border border-[#c9a227]/40 rounded-full px-4 py-1.5 mb-3">
              <span className="text-[#c9a227] text-[11px] font-bold uppercase tracking-[2px] font-mono">★ RESPALDO MEDIATICO ★</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3 leading-tight">
              Las estrellas de TV<br /><span className="text-[#c9a227]">nos avalan</span>
            </h2>
            <p className="text-slate-400 text-sm sm:text-base max-w-2xl mx-auto">
              Figuras reconocidas de la television respaldan el trabajo y la trayectoria de <span className="text-white font-semibold">Carlos Miami Fernandez</span>.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { id: 'Iyb71N9ZXUw', t: 0 },
              { id: 'uFf9kgco_4E', t: 0 },
              { id: 'da620Aq34RM', t: 4 },
              { id: 'DINRUdctWko', t: 0 },
            ].map((v, i) => (
              <div key={v.id} data-testid={`tv-video-${i + 1}`} className="aspect-video rounded-xl overflow-hidden bg-black border border-white/10 shadow-lg hover:border-[#c9a227]/50 hover:shadow-[0_10px_40px_rgba(201,162,39,0.2)] transition-all">
                <iframe
                  src={ytEmbedUrl(v.id, { start: v.t })}
                  title={`TV aval ${i + 1}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HISTORIA PERSONAL - VIDEO DESTACADO */}
      <section data-testid="historia-personal-section" className="px-4 py-16 border-t border-white/10 bg-[#1a1a2e]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#c9a227]/15 border border-[#c9a227]/40 rounded-full px-4 py-1.5 mb-3">
              <span className="text-[#c9a227] text-[11px] font-bold uppercase tracking-[2px] font-mono">★ HISTORIA PERSONAL ★</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3 leading-tight">
              Carlos Miami Fernandez<br /><span className="text-[#c9a227]">lleva mucho tiempo construyendo esta industria</span>
            </h2>
            <p className="text-slate-400 text-sm sm:text-base max-w-2xl mx-auto">
              Conoce de primera mano la historia del fundador de Club Viajemos y Travel Empire. Desde sus inicios hasta convertirse en el lider del equipo de agencias de viajes mas exitoso de Hotmart.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="aspect-video rounded-xl overflow-hidden bg-black border border-white/10 shadow-[0_10px_40px_rgba(201,162,39,0.2)]">
              <iframe
                src={ytEmbedUrl('shySZKJvhC8')}
                title="Carlos Miami Fernandez - Historia Personal"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MODAL PLAYER */}
      {active && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="video-modal"
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <button
            data-testid="close-video"
            onClick={() => setActive(null)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-black rounded-xl overflow-hidden shadow-[0_30px_100px_rgba(0,0,0,0.6)]">
              <video
                key={active.src}
                src={active.src}
                poster={POSTER}
                controls
                autoPlay
                playsInline
                className="w-full aspect-video"
              />
            </div>
            <p className="text-center text-slate-400 text-xs mt-3">
              Video #{String(active.num).padStart(2, '0')} de 27 · La Historia en Videos
            </p>
          </div>
        </div>
      )}

      {/* CTA FOOTER */}
      <footer className="py-12 px-4 bg-[#0f1629] border-t border-white/10 text-center">
        <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">¿Listo para empezar tu propia historia?</h2>
        <p className="text-slate-400 text-sm mb-6">Accede al Manual Completo Travel Empire + viaje a Orlando por solo $27 USD.</p>
        <a
          href="/manual27"
          data-testid="cta-to-manual27"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#c9a227] to-[#e0b835] text-[#0a0a14] font-black px-8 py-3.5 rounded-lg uppercase tracking-wider text-sm hover:scale-[1.02] transition-transform shadow-[0_10px_40px_rgba(201,162,39,0.4)]"
        >
          Acceder por $27 USD
        </a>
      </footer>
    </div>
  );
};

export default HistoriaEnVideosPage;
